import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { LoginPage } from "./auth/LoginPage";
import { Layout } from "./components/Layout";
import { Spinner } from "./components/ui";
import { RecordsPage } from "./pages/RecordsPage";

// Records is the page people live in, so it stays in the main bundle. The two
// chart pages pull in the whole charting library — several hundred kB — which
// has no business being downloaded over mobile data by someone who only came
// to tick a record as Done. Bulk Paste, Database, and Settings are occasional
// enough to split for the same reason.
const DashboardPage = lazy(async () => ({
  default: (await import("./pages/DashboardPage")).DashboardPage,
}));
const StatisticsPage = lazy(async () => ({
  default: (await import("./pages/StatisticsPage")).StatisticsPage,
}));
const BulkPastePage = lazy(async () => ({
  default: (await import("./pages/BulkPastePage")).BulkPastePage,
}));
const DatabasePage = lazy(async () => ({
  default: (await import("./pages/DatabasePage")).DatabasePage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import("./pages/SettingsPage")).SettingsPage,
}));
const VpsPage = lazy(async () => ({
  default: (await import("./pages/VpsPage")).VpsPage,
}));

function PageFallback() {
  return (
    <div className="flex justify-center py-20">
      <Spinner className="size-6 text-fg-muted" />
    </div>
  );
}

export function App() {
  const { operator, checking } = useAuth();

  // A stored token is verified against the server before the shell renders;
  // showing the app first and then bouncing to login would flash broken pages.
  if (checking) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6 text-fg-muted" />
      </div>
    );
  }

  if (!operator) return <LoginPage />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/records" element={<RecordsPage />} />
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<PageFallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="/bulk-paste"
          element={
            <Suspense fallback={<PageFallback />}>
              <BulkPastePage />
            </Suspense>
          }
        />
        <Route
          path="/statistics"
          element={
            <Suspense fallback={<PageFallback />}>
              <StatisticsPage />
            </Suspense>
          }
        />
        <Route
          path="/database"
          element={
            <Suspense fallback={<PageFallback />}>
              <DatabasePage />
            </Suspense>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<PageFallback />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="/vps"
          element={
            <Suspense fallback={<PageFallback />}>
              <VpsPage />
            </Suspense>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
